import React, { useState, useEffect, useMemo } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useParams } from 'react-router';
import { useLocation, useHistory } from 'react-router';
import queryString from 'query-string';
import _get from 'lodash/get';
import { useDispatch, useData, useClearFetch, useStatus, combineStatuses } from 'resift';
import makeCreateCategoryFieldFetch from 'store/attribute/actions/createCategoryFieldFetch';
import makeUpdateCategoryFieldFetch from 'store/attribute/actions/updateCategoryFieldFetch';
import useEntityTypes from 'helpers/useEntityTypes';
import makeEntityTypesFetch from 'store/configuration/actions/entityTypesFetch';
import makeGetUserFieldsFetch from 'fetches/makeGetUserFieldsFetch';
import makeGetFieldsFetch from 'fetches/makeGetFieldsFetch';
import * as Types from 'pages/AttributesEdit/typings';
import useAttributesEditDialog from 'pages/AttributesEdit/helpers/useAttributesEditDialog';
import { HOME_URL, ATTRIBUTES_URL } from 'components/Router/consts';

import Dialog from '@material-ui/core/Dialog';
import Typography from '@material-ui/core/Typography';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Button from '@material-ui/core/Button';
import AttributesChooseNew from 'pages/AttributesNew/AttributesChooseNew';
import AttributesCreateNew from 'pages/AttributesNew/AttributesCreateNew';
import AttributesEdit from 'pages/AttributesEdit';
import BackButton from 'components/Page/BackButton';
import Loader from 'components/Loader';

function AttributesEditModal() {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  let { entityTypeKey, baseField } = useParams<{
    entityTypeKey: string;
    baseField: string;
  }>();
  const { isOpen, close } = useAttributesEditDialog(entityTypeKey);
  const classes = useStyles();
  const [currentSlideIndex, setSlideIndex] = useState(0);
  const query = useMemo(() => queryString.parse(location.search), [location.search]);
  const parentObjectKey = _get(query, 'parentObjectKey');
  const newAttribute = query.newAttribute;
  const subAttribute = Boolean(parentObjectKey);
  const newAttributeTitle = subAttribute ? 'Create New Sub-Attribute' : 'Create New Attribute';
  const title =
    currentSlideIndex === 0 || currentSlideIndex === 1 ? newAttributeTitle : 'Edit Attribute';
  const createField = makeCreateCategoryFieldFetch();
  const createFieldStatus = useStatus(createField);
  const updateField = makeUpdateCategoryFieldFetch();
  const updateFieldStatus = useStatus(updateField);
  const getFields = makeGetFieldsFetch();
  const fieldsStatus = useStatus(getFields);
  const newAttributeResponse = useData(createField);
  const entityTypesFetch = makeEntityTypesFetch();
  const entityTypesFetchStatus = useStatus(entityTypesFetch);
  const getUserFields = makeGetUserFieldsFetch();
  const userFieldsStatus = useStatus(getUserFields);
  const entityTypes = useEntityTypes();
  const entityType = _get(entityTypes, entityTypeKey);
  const entityTypeId = _get(entityType, 'entityTypeId');
  const clearFetch = useClearFetch();
  const combinedStatus = combineStatuses(
    createFieldStatus | 1,
    updateFieldStatus | 1,
    entityTypesFetchStatus | 1,
    userFieldsStatus | 1,
    fieldsStatus | 1,
  );

  useEffect(() => {
    if (location.pathname.includes('edit')) {
      setSlideIndex(2);
    } else if (location.pathname.includes('/create/')) {
      setSlideIndex(1);
    } else {
      setSlideIndex(0);
    }
  }, [location]);

  const handleUpdateParentField = async () => {
    const parentField = _get(entityType, `_fields[${parentObjectKey}]`);
    const parentPayload = {
      ...parentField,
      fields: [...parentField.fields, newAttributeResponse.objectKey],
    };

    await dispatch(updateField(parentField.id, parentPayload));
    await dispatch(getUserFields());
    await dispatch(entityTypesFetch());
    await dispatch(getFields(entityTypeKey));
    history.push(
      `${HOME_URL}${ATTRIBUTES_URL}/${newAttributeResponse.entityField}/edit/${newAttributeResponse.objectKey}?newAttribute=true`,
    );
  };

  const handleCreateAttribute = async (values: Types.AttributesEditFormValues) => {
    let payload = {
      ...values.client,
      baseField,
      entityTypeId,
      ...(subAttribute && { parentFieldId: _get(entityType, `_fields[${parentObjectKey}].id`) }),
    };
    dispatch(createField(payload));
  };

  const handleClearFetches = () => {
    clearFetch(createField);
    clearFetch(updateField);
  };

  const handleRedirect = async () => {
    await dispatch(entityTypesFetch());
    await dispatch(getUserFields());
    await dispatch(getFields(entityTypeKey));
    history.push(
      `${HOME_URL}${ATTRIBUTES_URL}/${newAttributeResponse.entityField}/edit/${newAttributeResponse.objectKey}?newAttribute=true`,
    );
  };

  const currentSlideContent =
    currentSlideIndex === 0 ? (
      <AttributesChooseNew />
    ) : currentSlideIndex === 1 ? (
      <AttributesCreateNew
        newAttributeResponse={newAttributeResponse}
        onClearFetches={handleClearFetches}
        onRedirect={handleRedirect}
        onSubmit={handleCreateAttribute}
        onUpdateParentField={handleUpdateParentField}
      />
    ) : currentSlideIndex === 2 ? (
      <AttributesEdit />
    ) : null;

  return (
    <Dialog
      classes={{ paper: classes.paper }}
      open={isOpen}
      onClose={close}
      fullScreen
      aria-labelledby="edit-attributes-modal"
    >
      <AppBar className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          {currentSlideIndex !== 2 && <BackButton iconClassName={classes.backButtonIcon} goBack />}
          <Typography
            id="edit-attributes-modal"
            variant="h6"
            color="inherit"
            className={classes.flex}
          >
            {title}
          </Typography>
          <Button color="inherit" onClick={close} className={classes.cancelButton}>
            {newAttribute ? 'Done' : 'Cancel'}
          </Button>
        </Toolbar>
      </AppBar>
      <Loader status={combinedStatus}>{() => currentSlideContent}</Loader>
    </Dialog>
  );
}

const useStyles = makeStyles((theme: any) => ({
  appBar: {
    position: 'relative',
    backgroundColor: '#2f2f32',
  },
  toolbar: {
    paddingRight: theme.spacing(4),
  },
  cancelButton: {
    marginRight: theme.spacing(1),
  },
  flex: {
    flex: 1,
  },
  slideContainer: {
    flex: '1 1 auto',
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
    minHeight: 400,
    '& > div': {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
  },
  slide: {
    position: 'absolute',
    width: '100%',
    minWidth: '100%',
    height: '100%',
    transition: theme.transitions.create(['left', 'opacity'], {
      duration: theme.transitions.duration.complex,
    }),
    display: 'flex',
    flexDirection: 'column',
  },
  leftSlide: {
    left: 0,
    opacity: 1,
  },
  leftSlideHidden: {
    left: '-100%',
    opacity: 0,
  },
  rightSlide: {
    left: 0,
    opacity: 1,
  },
  rightSlideHidden: {
    left: '100%',
    opacity: 0,
  },
  paper: {
    background: '#F9FBFD',
  },
  backButtonIcon: {
    color: theme.palette.common.white,
  },
}));

export default AttributesEditModal;
